import apiClient from './apiClient';
import { ApiResponse } from '../types';
import { Expiry, ExpiriesResponse, HolidaysResponse } from '../types/expiry';

interface RawExpiry {
  expiry_date: string;
  is_monthly: boolean;
  lot_size: number;
  freeze_limit: number;
}

interface RawExpiriesPayload {
  index: string;
  expiry_type: 'weekly' | 'monthly';
  expiries: RawExpiry[];
}

interface RawHolidaysPayload {
  holidays: Record<string, string>;
}

const TTL_MS = 60 * 60 * 1000;          // 1 hour
const STORAGE_PREFIX = 'refdata:';
const inMemoryCache = new Map<string, { fetchedAt: number; value: unknown }>();

interface CacheEnvelope<T> {
  fetchedAt: number;
  value: T;
}

/**
 * Two-layer cache: in-memory Map → localStorage → fetchFn.
 * On fetch failure, returns stale cached value if available; otherwise rethrows.
 * `localStorage` writes are best-effort — a quota error or disabled storage
 * silently degrades to memory-only caching.
 */
async function cachedFetch<T>(key: string, ttlMs: number, fetchFn: () => Promise<T>): Promise<T> {
  const now = Date.now();

  const memHit = inMemoryCache.get(key);
  if (memHit && now - memHit.fetchedAt < ttlMs) {
    return memHit.value as T;
  }

  let lsHit: CacheEnvelope<T> | null = null;
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    if (raw) lsHit = JSON.parse(raw) as CacheEnvelope<T>;
  } catch {
    lsHit = null;
  }
  if (lsHit && now - lsHit.fetchedAt < ttlMs) {
    inMemoryCache.set(key, lsHit);
    return lsHit.value;
  }

  try {
    const value = await fetchFn();
    const envelope: CacheEnvelope<T> = { fetchedAt: now, value };
    inMemoryCache.set(key, envelope);
    try {
      localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(envelope));
    } catch {
      // quota / disabled — fall back to memory-only
    }
    return value;
  } catch (err) {
    // Stale-on-failure: better to render with old data than crash the view.
    if (memHit) return memHit.value as T;
    if (lsHit) {
      inMemoryCache.set(key, lsHit);
      return lsHit.value;
    }
    throw err;
  }
}

function normaliseExpiries(raw: RawExpiriesPayload): ExpiriesResponse {
  return {
    index: raw.index,
    expiryType: raw.expiry_type,
    expiries: (raw.expiries || []).map<Expiry>(e => ({
      expiryDate: e.expiry_date,
      isMonthly: e.is_monthly,
      lotSize: e.lot_size,
      freezeLimit: e.freeze_limit,
    })),
  };
}

export async function getExpiries(
  index: string,
  expiryType: 'weekly' | 'monthly',
  count: number = 8,
): Promise<ExpiriesResponse> {
  // v2: added `freezeLimit` to the normalised shape — bumping the key
  // invalidates pre-update entries in users' localStorage immediately,
  // otherwise the Expiry Days table renders "—" for up to 1h after deploy.
  const key = `expiries:v2:${index}:${expiryType}:${count}`;
  return cachedFetch(key, TTL_MS, async () => {
    const params = new URLSearchParams({
      type: 'expiries',
      index,
      expiry_type: expiryType,
      count: String(count),
    });
    const response = await apiClient.get<ApiResponse<RawExpiriesPayload>>(
      `/reference-data?${params.toString()}`,
    );
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch expiries');
    }
    return normaliseExpiries(response.data);
  });
}

export async function getHolidays(): Promise<HolidaysResponse> {
  const key = 'holidays:all';
  return cachedFetch(key, TTL_MS, async () => {
    const response = await apiClient.get<ApiResponse<RawHolidaysPayload>>(
      `/reference-data?type=holidays`,
    );
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch holidays');
    }
    return { holidays: response.data.holidays || {} };
  });
}

/** Test/debug helper — clears both cache layers for the reference-data namespace. */
export function clearReferenceDataCache(): void {
  inMemoryCache.clear();
  try {
    Object.keys(localStorage)
      .filter(k => k.startsWith(STORAGE_PREFIX))
      .forEach(k => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}
